import {create} from "zustand";
import React from "react";

export type ModalTone = "neutral" | "success" | "error" | "warning" | "info";
export type ModalResult = "primary" | "secondary" | "dismiss";

export type ModalAction = {
    label: string;
    onPress?: () => void | Promise<void>;
    disabled?: boolean;
    variant?: "primary" | "secondary";
    autoClose?: boolean;
};

export type ModalContent = React.ReactNode | (() => React.ReactNode);

export type AppModalConfig = {
    key?: string;

    tone?: ModalTone;
    title: React.ReactNode;
    content?: ModalContent;

    primaryAction?: ModalAction;
    secondaryAction?: ModalAction;

    isDismissable?: boolean;
    showCloseButton?: boolean;

    className?: string;
    closeLabel?: string;

    footerMessage?: React.ReactNode;
};

type ModalStore = {
    isOpen: boolean;
    config: AppModalConfig | null;
    resolver?: (r: ModalResult) => void;

    open: (config: AppModalConfig) => Promise<ModalResult>;
    close: (result?: ModalResult) => void;

    update: (patch: Partial<AppModalConfig>, key?: string) => void;
};

export const useModalStore = create<ModalStore>((set, get) => ({
    isOpen: false,
    config: null,
    resolver: undefined,

    open: (config) =>
        new Promise<ModalResult>((resolve) => {
            const prevResolver = get().resolver;
            if (get().isOpen && prevResolver) {
                prevResolver("dismiss");
            }

            set({isOpen: true, config, resolver: resolve});
        }),

    close: (result = "dismiss") => {
        const resolver = get().resolver;
        set({isOpen: false, config: null, resolver: undefined});
        resolver?.(result);
    },

    update: (patch, key) => {
        const current = get().config;
        if (!current) return;

        if (key && current.key && current.key !== key) return;

        set({config: {...current, ...patch}});
    },
}));